import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useT } from '@/i18n'
import { inventoryMessages } from '@/i18n/messages/inventory'
import { normalizeDrumCode } from './drumCode'
import { Dialog, INPUT, PrimaryButton } from './shared'

// The Shape Detection API (Chrome on Android, Edge). Not in TypeScript's DOM lib yet.
interface DetectedBarcode { rawValue: string }
interface BarcodeDetectorLike { detect(source: CanvasImageSource): Promise<DetectedBarcode[]> }
declare global {
  interface Window { BarcodeDetector?: new (opts: { formats: string[] }) => BarcodeDetectorLike }
}

/**
 * Scan a drum's QR with the in-app camera, for going through several drums in a row. Where the
 * browser can't detect barcodes (Safari), the phone's own camera app opens the label's URL
 * directly, and the code can always be typed.
 */
export function ScanDialog({ onClose }: { onClose: () => void }) {
  const t = useT(inventoryMessages)
  const navigate = useNavigate()
  const video = useRef<HTMLVideoElement>(null)
  const [supported] = useState(() => typeof window !== 'undefined' && Boolean(window.BarcodeDetector) && Boolean(navigator.mediaDevices?.getUserMedia))
  const [cameraError, setCameraError] = useState(false)
  const [typed, setTyped] = useState('')
  const [bad, setBad] = useState(false)

  const open = (raw: string) => {
    const code = normalizeDrumCode(raw)
    if (!code) { setBad(true); return false }
    onClose()
    navigate(`/d/${code}`)
    return true
  }

  useEffect(() => {
    if (!supported) return
    let stream: MediaStream | null = null
    let timer = 0
    let stopped = false
    const detector = new window.BarcodeDetector!({ formats: ['qr_code'] })
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then(async (s) => {
        if (stopped) { s.getTracks().forEach((tr) => tr.stop()); return }
        stream = s
        if (!video.current) return
        video.current.srcObject = s
        await video.current.play()
        const tick = async () => {
          if (stopped || !video.current) return
          try {
            const found = await detector.detect(video.current)
            for (const b of found) if (normalizeDrumCode(b.rawValue)) { open(b.rawValue); return }
          } catch { /* a frame that couldn't be read; try the next */ }
          timer = window.setTimeout(tick, 250)
        }
        void tick()
      })
      .catch(() => setCameraError(true))
    return () => { stopped = true; window.clearTimeout(timer); stream?.getTracks().forEach((tr) => tr.stop()) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported])

  return (
    <Dialog title={t('scanTitle')} hint={supported && !cameraError ? t('scanHint') : undefined} onClose={onClose}>
      {supported && !cameraError ? (
        <div className="relative overflow-hidden rounded-xl bg-black">
          <video ref={video} playsInline muted className="aspect-square w-full object-cover" />
          <div className="pointer-events-none absolute inset-[18%] rounded-2xl ring-4 ring-white/80" />
        </div>
      ) : (
        <p className="rounded-xl bg-neutral-100 p-3 text-sm text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{t('scanNoCamera')}</p>
      )}
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); open(typed) }}>
        <input value={typed} onChange={(e) => { setTyped(e.target.value); setBad(false) }} placeholder="MIH-…" autoCapitalize="characters"
          autoCorrect="off" spellCheck={false} aria-label={t('typeCode')} className={`${INPUT} font-mono`} />
        <PrimaryButton type="submit">{t('open')}</PrimaryButton>
      </form>
      {bad && <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{t('badCode')}</p>}
    </Dialog>
  )
}
